import { gql } from '@apollo/client'

export const CONTENT_LIST_QUERY = gql`
  query ContentList(
    $offset: Int = 0
    $limit: Int = 25
    $order: [[String]]
    $where: ContentWhereInput = {}
    $language: [String] = ["en", "_primary"]
  ) {
    contentList(offset: $offset, limit: $limit, order: $order, where: $where, language: $language) {
      count
      limit
      offset
      content {
        titleGroupId
        contentId
        contentType
        language
        title
      }
    }
  }
`

export const GET_COURSE_CONTENT = gql`
  query GetCourseContent($id: ID!) {
    contentList(where: { id: { eq: $id }, contentType: { eq: "course" } }) {
      content {
        id
        contentId
        contentType
        title
        description
        descriptionFormat
        shortDescription
        shortDescriptionFormat
        thirdPartyDescription
        path
        language
        primaryLanguage
        published
        publishedAtDate
        productionStatus
        contentSearchNoIndex
        excludeFromRecommendations
        createdAt
        updatedAt
        productType
        displayType
        seasonNumber
        episodeNumber
        director
        producer
        writer
        studio
        copyright
        instructor
        cast
        previewMediaId
        featureMediaId
        contentReferencesTo(types: ["course-module"]) {
          sortKey
          contentFrom {
            id
            contentId
            contentType
            title
            published
            contentReferencesTo(types: ["course-module-practice", "course-module-other"]) {
              contentReferenceType {
                type
              }
              sortKey
            }
          }
        }
        # Must include every ref type used by CourseContentReferencesSection (main) and
        # CourseContentReferencesExtendedSection. If you add a new content-ref section to course
        # detail, add its types here or existing refs won't load and saves will disappear after refetch.
        contentReferencesFrom(
          types: [
            "admin_category"
            "category"
            "rainbow-category"
            "centers"
            "marketing-centers"
            "content_origin"
            "genres"
            "interview-guest"
            "interview-host"
            "similar-videos"
            "third-party-genre"
            "product"
            "fitness-duration"
            "fitness-instructor"
            "fitness-level"
            "fitness-specialty"
            "fitness-style"
            "onboarding_choices"
            "video-collection"
            "yoga-duration"
            "yoga-focus"
            "yoga-level"
            "yoga-style"
            "yoga-teacher"
            "meditation-duration"
            "meditation-focus"
            "meditation-style"
            "meditation-teacher"
          ]
        ) {
          id
          contentReferenceType {
            type
          }
          contentTo(language: ["en", "_primary"]) {
            id
            contentId
            contentType
            title
            published
            titleGroupId
          }
        }
      }
    }
  }
`

/**
 * Fetch a single content by id for the Video Detail V2 page.
 * Includes content info and contentReferencesFrom (same set as legacy video detail).
 */
export const GET_VIDEO_CONTENT = gql`
  query GetVideoContent($id: ID!) {
    contentList(where: { id: { eq: $id } }, limit: 1) {
      content {
        id
        contentId
        contentType
        title
        description
        descriptionFormat
        shortDescription
        shortDescriptionFormat
        thirdPartyDescription
        path
        language
        primaryLanguage
        published
        publishedAtDate
        productionStatus
        contentSearchNoIndex
        excludeFromRecommendations
        createdAt
        updatedAt
        productType
        displayType
        seasonNumber
        episodeNumber
        director
        producer
        writer
        studio
        copyright
        instructor
        cast
        previewMediaId
        featureMediaId
        seoTitle
        seoDescription
        seoKeywords
        seoRobotsNoFollow
        seoRobotsNoIndex
        # Must include every ref type used by VideoContentReferencesSection (main), VideoContentReferencesExtendedSection,
        # and VideoContentReferencesYoreSection. If you add a new content-ref section to video detail,
        # add its types here or existing refs won't load and saves will disappear after refetch.
        contentReferencesFrom(
          types: [
            "admin_category"
            "category"
            "rainbow-category"
            "centers"
            "marketing-centers"
            "conscious-format"
            "conscious-topic"
            "product"
            "content_origin"
            "film-subject"
            "film-type"
            "fitness-duration"
            "fitness-instructor"
            "fitness-level"
            "fitness-specialty"
            "fitness-style"
            "genres"
            "interview-guest"
            "interview-host"
            "meditation-focus"
            "meditation-style"
            "meditation-teacher"
            "meditation-duration"
            "onboarding_choices"
            "recipe-diet"
            "recipe-type"
            "similar-videos"
            "third-party-genre"
            "video-collection"
            "video-topic"
            "yoga-duration"
            "yoga-focus"
            "yoga-level"
            "yoga-style"
            "yoga-teacher"
            "course-module-practice"
            "course-module-other"
            "series"
            "primary-course"
          ]
        ) {
          id
          contentReferenceType {
            type
          }
          contentTo(language: ["en", "_primary"]) {
            id
            contentId
            contentType
            title
            published
            titleGroupId
          }
        }
      }
    }
  }
`

export const GET_COURSE_MODULE_CONTENT = gql`
  query GetCourseModuleContent($id: ID!) {
    contentList(where: { id: { eq: $id } }) {
      content {
        id
        contentId
        contentType
        title
        description
        descriptionFormat
        shortDescription
        shortDescriptionFormat
        thirdPartyDescription
        path
        language
        primaryLanguage
        published
        publishedAtDate
        productionStatus
        contentSearchNoIndex
        excludeFromRecommendations
        createdAt
        updatedAt
        productType
        displayType
        seasonNumber
        episodeNumber
        director
        producer
        writer
        studio
        copyright
        instructor
        cast
      }
    }
  }
`

export const GET_COURSE_MODULE_CONTENT_REFERENCES = gql`
  query GetCourseModuleContentReferences($id: ID!, $language: [String]!) {
    contentList(where: { id: { eq: $id } }) {
      content {
        id
        contentReferencesTo(types: ["course-module-practice", "course-module-other"]) {
          id
          contentReferenceType {
            type
          }
          sortKey
          contentFrom(language: $language) {
            id
            contentId
            contentType
            title
            published
            imageContentRelationships(
              language: $language
              types: ["d6-keyart-with-text", "d6-keyart-no-text"]
            ) {
              id
              type
              image {
                id
                url
              }
            }
          }
        }
      }
    }
  }
`

export const GET_PARENT_COURSE_ID = gql`
  query GetParentCourseId($id: ID!) {
    contentList(where: { id: { eq: $id } }) {
      content {
        id
        contentReferencesFrom(types: ["course-module"]) {
          id
          contentTo {
            id
            contentId
            contentType
            title
          }
        }
      }
    }
  }
`

export const GET_SERIES_REFERENCES = gql`
  query GetSeriesWithLatestEpisodeAdminCategory($seriesContentId: Int!) {
    contentList(where: { contentId: { eq: $seriesContentId }, contentType: { eq: "series" } }) {
      content {
        contentId
        contentType
        title
        language
        episodeNumber
        seasonNumber
        producer
        writer
        episodesCount
        seasonsCount
        director
        instructor
        studio
        copyright
        cast
        productType
        contentReferencesFrom {
          id
          fromContentContentId
          fromContentContentType
          toContentContentId
          toContentContentType
          contentReferenceType {
            type
          }
          contentTo {
            id
            contentId
            contentType
            subType
            bundle
            title
            published
            titleGroupId
          }
        }
      }
    }
  }
`

export const GET_COURSE_REFERENCES = gql`
  query GetCourseReferences($courseContentId: Int!) {
    contentList(where: { contentId: { eq: $courseContentId }, contentType: { eq: "course" } }) {
      content {
        contentId
        contentType
        title
        contentReferencesFrom {
          id
          contentReferenceType {
            type
          }
          contentTo {
            id
            contentId
            contentType
            title
            published
            titleGroupId
          }
        }
      }
    }
  }
`

export const GET_PRODUCT_REFERENCES = gql`
  query GetProductReferences($productContentId: Int!) {
    contentList(where: { contentId: { eq: $productContentId }, contentType: { eq: "product" } }) {
      content {
        contentId
        contentType
        title
        language
        contentReferencesFrom {
          id
          fromContentContentId
          fromContentContentType
          toContentContentId
          toContentContentType
          contentFrom {
            id
            contentId
            contentType
            title
            language
            published
            path
          }
          contentTo {
            id
            contentId
            contentType
            title
            language
            published
            path
          }
        }
        contentReferencesTo {
          id
          fromContentContentId
          fromContentContentType
          toContentContentId
          toContentContentType
          contentFrom {
            id
            contentId
            contentType
            title
            language
            published
            path
          }
          contentTo {
            id
            contentId
            contentType
            title
            language
            published
            path
          }
        }
      }
    }
  }
`

export const UPDATE_COURSE_CONTENT = gql`
  mutation UpdateCourseContent($content: ContentInput!, $generatePath: Boolean) {
    contentUpdate(content: $content, generatePath: $generatePath) {
      content {
        id
        contentId
        contentType
        title
        description
        descriptionFormat
        shortDescription
        shortDescriptionFormat
        thirdPartyDescription
        path
        language
        published
        publishedAtDate
        productionStatus
        contentSearchNoIndex
        excludeFromRecommendations
        createdAt
        updatedAt
        productType
        displayType
        seasonNumber
        episodeNumber
        director
        producer
        writer
        studio
        copyright
        instructor
        cast
        previewMediaId
        featureMediaId
      }
    }
  }
`

/**
 * Same as UPDATE_COURSE_CONTENT - contentUpdate is content-type agnostic.
 * Used by Video Detail V2 for saving content info.
 */
export const UPDATE_VIDEO_CONTENT = gql`
  mutation UpdateVideoContent($content: ContentInput!, $generatePath: Boolean) {
    contentUpdate(content: $content, generatePath: $generatePath) {
      content {
        id
        contentId
        contentType
        title
        description
        descriptionFormat
        shortDescription
        shortDescriptionFormat
        thirdPartyDescription
        path
        language
        published
        publishedAtDate
        productionStatus
        contentSearchNoIndex
        excludeFromRecommendations
        createdAt
        updatedAt
        productType
        displayType
        seasonNumber
        episodeNumber
        director
        producer
        writer
        studio
        copyright
        instructor
        cast
      }
    }
  }
`

export const GET_CONTENT_REFERENCE_TYPES = gql`
  query GetContentReferenceTypes {
    contentReferenceTypesList {
      contentReferenceTypes {
        id
        type
      }
    }
  }
`

/**
 * Update the sort order of content references
 *
 * IMPORTANT: The deleteOmittedReferences parameter controls deletion behavior:
 * - false (default): Only create/update the provided references, leave other references intact
 * - true: DELETE all existing references of the same type(s) that aren't in this batch
 *
 * Set to true when you want to replace ALL references of a specific type (e.g., reordering modules).
 * Set to false when updating sort order for a subset to avoid deleting other references.
 */
export const UPDATE_CONTENT_REFERENCES_SORT_ORDER = gql`
  mutation UpdateContentReferencesSortOrder(
    $contentReferences: [ContentReferenceCreateInput!]!
    $deleteOmittedReferences: Boolean = false
  ) {
    contentReferenceCreateMany(
      contentReferences: $contentReferences
      deleteOmittedReferences: $deleteOmittedReferences
    ) {
      contentReferences {
        id
        sortKey
        fromContentContentId
        fromContentContentType
        toContentContentId
        toContentContentType
      }
    }
  }
`

export const GET_CONTENT_TRANSLATIONS = gql`
  query GetContentTranslations($contentId: Int!, $contentType: String!) {
    contentList(where: { contentId: { eq: $contentId }, contentType: { eq: $contentType } }) {
      content {
        id
        contentId
        contentType
        language
        primaryLanguage
        title
        published
      }
    }
  }
`

export const GET_CONTENT_TRANSCRIPT = gql`
  query GetContentTranscript($contentId: Int!) {
    contentList(where: { contentId: { eq: $contentId } }, limit: 1) {
      content {
        id
        contentId
        featureMedia {
          mediaAssets {
            aiMetadata {
              summary
              transcript
            }
          }
        }
      }
    }
  }
`

/**
 * Check if a video is linked to by any course-module
 * Used to conditionally show the Featured Course section
 * Videos are linked via course-module-other or course-module-practice reference types
 */
export const CHECK_VIDEO_COURSE_MODULE_LINK = gql`
  query CheckVideoCourseModuleLink($videoId: ID!) {
    contentList(where: { id: { eq: $videoId } }) {
      content {
        id
        contentId
        contentReferencesFrom(types: ["course-module-other", "course-module-practice"]) {
          id
          contentTo {
            id
            contentId
            title
            contentType
          }
        }
      }
    }
  }
`

/**
 * Get existing primary-course reference for a video
 * Used to populate the initial dropdown value
 */
export const GET_VIDEO_PRIMARY_COURSE = gql`
  query GetVideoPrimaryCourse($videoId: ID!) {
    contentList(where: { id: { eq: $videoId } }) {
      content {
        id
        contentId
        primaryCourse: contentReferencesFrom(types: ["primary-course"]) {
          id
          contentTo {
            id
            contentId
            contentType
            title
            published
          }
        }
      }
    }
  }
`
